import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useBlocker } from "@tanstack/react-router";

import UnsavedChangesDialog from "~/components/miscellaneous/unsaved-changes-dialog";

interface UnsavedChanges {
  isDirty: boolean;
  onDiscard?: () => void;
}

interface UnsavedChangesContextValue {
  unsavedChanges: UnsavedChanges;
  setUnsavedChanges: (unsavedChanges: UnsavedChanges) => void;
  checkUnsavedChanges: () => boolean;
}

const UnsavedChangesContext = createContext<
  UnsavedChangesContextValue | undefined
>(undefined);

function unsavedChangesReducer(
  state: UnsavedChanges,
  action: UnsavedChanges,
): UnsavedChanges {
  return {
    ...state,
    ...action,
  };
}

export function UnsavedChangesProvider({
  children,
  detectRouteChanges = true,
}: {
  children: ReactNode;
  detectRouteChanges?: boolean;
}) {
  const [unsavedChanges, dispatch] = useReducer(unsavedChangesReducer, {
    isDirty: false,
  });

  const unsavedChangesRef = useRef(unsavedChanges);

  const [isOpen, setIsOpen] = useState(false);

  const { reset, status, proceed } = useBlocker({
    condition: unsavedChanges.isDirty && detectRouteChanges,
  });

  const setUnsavedChanges = useCallback(
    (changes: UnsavedChanges) => {
      if (changes.isDirty !== unsavedChanges.isDirty) {
        dispatch(changes);
        unsavedChangesRef.current = { ...unsavedChanges, ...changes };
      }
    },
    [unsavedChanges],
  );
  const checkUnsavedChanges = useCallback(() => {
    if (unsavedChangesRef.current.isDirty) {
      setIsOpen(true);
      return true;
    } else {
      setIsOpen(false);
      return false;
    }
  }, []);

  useEffect(() => {
    window.onbeforeunload = (e) => {
      if (unsavedChanges.isDirty) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return null;
      }
    };
  }, [unsavedChanges]);

  useEffect(() => {
    if (status === "blocked") {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [status]);

  useEffect(() => {
    unsavedChangesRef.current = unsavedChanges;
  }, [unsavedChanges]);

  const context = {
    unsavedChanges,
    setUnsavedChanges,
    checkUnsavedChanges,
  };

  return (
    <UnsavedChangesContext.Provider value={context}>
      <UnsavedChangesDialog
        isOpen={isOpen}
        onDiscard={() => {
          setIsOpen(false);
          if (proceed) {
            proceed();
          }
          setTimeout(() => {}, 0);
        }}
        onContinue={() => {
          setIsOpen(false);
          if (reset) {
            reset();
          }
        }}
      />
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export const useUnsavedChanges = (): UnsavedChangesContextValue => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error(
      "useUnsavedChanges must be used within an UnsavedChangesProvider",
    );
  }
  return context;
};
